import {
  Checkbox,
  RadioButton,
  RadioButtonGroup,
  TextInput,
} from '@carbon/react';
import classNames from 'classnames';
import { format0, parseNumericValue } from 'utils/number';
import type {
  AspectRatioOption,
  ElementDescriptor,
  ElementSize,
  ExportImageOptions,
  OutputFormat,
} from './types';
import style from './export-image-options-fields.module.scss';

/**
 * One aspect-ratio radio option (value + display label). Callers pass the list they
 * want to offer — the single-chart drawer keeps its four; the bulk content-export
 * modal passes the full six (`ALL_ASPECT_RATIO_OPTIONS`).
 */
export interface AspectRatioChoice {
  value: AspectRatioOption;
  labelText: string;
}

/**
 * The single-chart drawer's aspect-ratio set (unchanged from its original inline list).
 * Also the default when a caller doesn't pass its own.
 */
export const ASPECT_RATIO_OPTIONS: AspectRatioChoice[] = [
  { value: '16:9', labelText: '16:9 (1,920 × 1,080)' },
  { value: '1:1', labelText: '1:1 (1,600 × 1,600)' },
  { value: 'current', labelText: 'Current Chart Size' },
  { value: 'custom', labelText: 'Custom' },
];

/**
 * The full aspect-ratio set (all six), for the bulk content-export modal — adds 4:3
 * and 3:2 beside the drawer's four (their preset px live in `PRESET_DIMENSIONS`).
 */
export const ALL_ASPECT_RATIO_OPTIONS: AspectRatioChoice[] = [
  { value: '16:9', labelText: '16:9 (1,920 × 1,080)' },
  { value: '4:3', labelText: '4:3 (1,600 × 1,200)' },
  { value: '3:2', labelText: '3:2 (1,800 × 1,200)' },
  { value: '1:1', labelText: '1:1 (1,600 × 1,600)' },
  { value: 'current', labelText: 'Current Chart Size' },
  { value: 'custom', labelText: 'Custom' },
];

// `previewFontPx` is illustrative only — kept independent of the export-time
// ELEMENT_SIZE_MULTIPLIER so the tiles stay visually balanced in the panel.
const ELEMENT_SIZE_OPTIONS: Array<{
  value: ElementSize;
  labelText: string;
  previewFontPx: number;
}> = [
  { value: 'small', labelText: 'Small', previewFontPx: 14 },
  { value: 'medium', labelText: 'Medium', previewFontPx: 18 },
  { value: 'large', labelText: 'Large', previewFontPx: 22 },
  { value: 'xlarge', labelText: 'XL', previewFontPx: 26 },
];

const OUTPUT_FORMAT_OPTIONS: Array<{ value: OutputFormat; labelText: string }> =
  [
    { value: 'png', labelText: 'PNG' },
    { value: 'jpeg', labelText: 'JPEG' },
  ];

const PREVIEW_MAX_EDGE = 130;

// Hard cap on custom export dimensions — beyond ~4000px browsers struggle to
// encode the PNG and label scaling is already past the legibility point.
export const MAX_CUSTOM_PX = 4000;

/**
 * A validation message for a custom width/height, or `null` when it's valid.
 *
 * @param {number | null} value - The entered dimension (null while blank/invalid).
 * @return {string | null} The error text, or null when valid.
 */
export function customDimensionError(value: number | null): string | null {
  if (value === null || !Number.isFinite(value) || value <= 0) {
    return 'Must be greater than 0';
  }
  if (value > MAX_CUSTOM_PX) return `Must be ${MAX_CUSTOM_PX} or less`;
  return null;
}

/**
 * Whether a custom width/height is valid (in range, > 0).
 *
 * @param {number | null} value - The entered dimension.
 * @return {boolean} True when valid.
 */
export function isCustomDimensionValid(value: number | null): boolean {
  return customDimensionError(value) === null;
}

/**
 * The aspect-ratio of the preview box (and whether it can be shown) for the chosen
 * ratio. `current` needs the live chart size; `custom` needs valid dimensions.
 *
 * @param {AspectRatioOption} ratio - The selected aspect ratio.
 * @param {number | null} customWidth - Custom width, when ratio is `custom`.
 * @param {number | null} customHeight - Custom height, when ratio is `custom`.
 * @param {{width: number, height: number} | null} liveSize - The live chart size (for `current`).
 * @return {{width: number, height: number} | null} The preview box px, or null when it can't render.
 */
function previewDimensions(
  ratio: AspectRatioOption,
  customWidth: number | null,
  customHeight: number | null,
  liveSize: { width: number; height: number } | null
): { width: number; height: number } | null {
  let aspectW = 0;
  let aspectH = 0;
  switch (ratio) {
    case '16:9':
      aspectW = 16;
      aspectH = 9;
      break;
    case '4:3':
      aspectW = 4;
      aspectH = 3;
      break;
    case '3:2':
      aspectW = 3;
      aspectH = 2;
      break;
    case '1:1':
      aspectW = 1;
      aspectH = 1;
      break;
    case 'current':
      if (!liveSize) return null;
      aspectW = liveSize.width;
      aspectH = liveSize.height;
      break;
    case 'custom':
      if (
        customWidth === null ||
        customHeight === null ||
        !isCustomDimensionValid(customWidth) ||
        !isCustomDimensionValid(customHeight)
      ) {
        return null;
      }
      aspectW = customWidth;
      aspectH = customHeight;
      break;
  }
  if (aspectW <= 0 || aspectH <= 0) return null;
  if (aspectW >= aspectH) {
    return {
      width: PREVIEW_MAX_EDGE,
      height: (PREVIEW_MAX_EDGE * aspectH) / aspectW,
    };
  }
  return {
    width: (PREVIEW_MAX_EDGE * aspectW) / aspectH,
    height: PREVIEW_MAX_EDGE,
  };
}

/**
 * The caption beneath the preview box for the chosen ratio.
 *
 * @param {AspectRatioOption} ratio - The selected aspect ratio.
 * @param {number | null} customWidth - Custom width, when ratio is `custom`.
 * @param {number | null} customHeight - Custom height, when ratio is `custom`.
 * @return {string} The caption text.
 */
function previewCaption(
  ratio: AspectRatioOption,
  customWidth: number | null,
  customHeight: number | null
): string {
  if (ratio === 'custom') {
    if (
      customWidth === null ||
      customHeight === null ||
      !isCustomDimensionValid(customWidth) ||
      !isCustomDimensionValid(customHeight)
    ) {
      return 'Enter valid dimensions';
    }
    return `${format0(customWidth)} × ${format0(customHeight)}`;
  }
  if (ratio === 'current') return 'Matches live chart';
  return ratio;
}

export interface ExportImageOptionsFieldsProps {
  /** The current options (controlled). */
  value: ExportImageOptions;
  /** Called with the next options on any field change. */
  onChange: (next: ExportImageOptions) => void;
  /** The chart's element descriptors — one Chart Elements checkbox per entry (omitted when empty). */
  elementDescriptors: ElementDescriptor[];
  /** The formats to offer; the Output Format selector shows only when there's a choice (length > 1). */
  supportedFormats: OutputFormat[];
  /** Aspect-ratio choices to offer; defaults to the drawer's four. */
  aspectOptions?: AspectRatioChoice[];
  /** The live chart size, for the `current` preview + caption; `null` when there's no single live size. */
  liveSize?: { width: number; height: number } | null;
}

/**
 * The controlled image-export option fields — Aspect Ratio (+ live preview + Custom
 * width/height), Element Size, Chart Elements, and (when >1 format) Output Format.
 * Extracted verbatim from the single-chart Export Image drawer so both the drawer and
 * the bulk content-export modal render the identical UI over one `ExportImageOptions`
 * value. Purely presentational: the parent owns the value + the Cancel/Export actions.
 *
 * @param {ExportImageOptionsFieldsProps} props - The controlled value + change handler + chart-derived descriptors/formats.
 * @return {JSX.Element} The option fields.
 */
export function ExportImageOptionsFields({
  value,
  onChange,
  elementDescriptors,
  supportedFormats,
  aspectOptions = ASPECT_RATIO_OPTIONS,
  liveSize = null,
}: ExportImageOptionsFieldsProps) {
  const updateOptions = (patch: Partial<ExportImageOptions>) => {
    onChange({ ...value, ...patch });
  };

  const toggleElement = (key: string, checked: boolean) => {
    onChange({
      ...value,
      elements: { ...value.elements, [key]: checked },
    });
  };

  const handleCustomDimensionChange = (
    inputValue: string,
    dimension: 'customWidth' | 'customHeight'
  ) => {
    updateOptions({ [dimension]: parseNumericValue(inputValue) });
  };

  const preview = previewDimensions(
    value.aspectRatio,
    value.customWidth,
    value.customHeight,
    liveSize
  );

  return (
    <>
      <div className={style.section}>
        <div className={style.sectionTitle}>Aspect Ratio</div>
        <div className={style.aspectRatioRow}>
          <div className={style.aspectRatioRadios}>
            <RadioButtonGroup
              legendText=""
              name="export-image-aspect-ratio"
              orientation="vertical"
              valueSelected={value.aspectRatio}
              onChange={(next) =>
                updateOptions({ aspectRatio: next as AspectRatioOption })
              }
            >
              {aspectOptions.map((opt) => (
                <RadioButton
                  key={opt.value}
                  value={opt.value}
                  labelText={opt.labelText}
                  id={`export-image-aspect-${opt.value}`}
                />
              ))}
            </RadioButtonGroup>
          </div>

          <div className={style.previewWrap}>
            <div className={style.previewStage}>
              {preview && (
                <div
                  className={style.previewRect}
                  style={{
                    width: `${preview.width}px`,
                    height: `${preview.height}px`,
                  }}
                />
              )}
            </div>
            <div className={style.previewCaption}>
              {previewCaption(
                value.aspectRatio,
                value.customWidth,
                value.customHeight
              )}
            </div>
          </div>
        </div>

        {value.aspectRatio === 'custom' && (
          <div className={style.customRow}>
            <TextInput
              id="export-image-custom-width"
              labelText="Width (px)"
              value={
                value.customWidth === null ? '' : format0(value.customWidth)
              }
              onChange={(e) =>
                handleCustomDimensionChange(e.target.value, 'customWidth')
              }
              invalid={!isCustomDimensionValid(value.customWidth)}
              invalidText={customDimensionError(value.customWidth) ?? undefined}
            />
            <TextInput
              id="export-image-custom-height"
              labelText="Height (px)"
              value={
                value.customHeight === null ? '' : format0(value.customHeight)
              }
              onChange={(e) =>
                handleCustomDimensionChange(e.target.value, 'customHeight')
              }
              invalid={!isCustomDimensionValid(value.customHeight)}
              invalidText={
                customDimensionError(value.customHeight) ?? undefined
              }
            />
          </div>
        )}
      </div>

      <div className={style.section}>
        <div className={style.sectionTitle}>Element Size</div>
        <div className={style.elementSizeGrid}>
          {ELEMENT_SIZE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={classNames(style.elementSizeButton, {
                [style.elementSizeActive]: value.elementSize === opt.value,
              })}
              onClick={() => updateOptions({ elementSize: opt.value })}
              aria-pressed={value.elementSize === opt.value}
            >
              <span
                className={style.elementSizeSample}
                style={{ fontSize: `${opt.previewFontPx}px` }}
              >
                Aa
              </span>
              <span className={style.elementSizeLabel}>{opt.labelText}</span>
            </button>
          ))}
        </div>
        <div className={style.sectionHelp}>
          Scales text, line widths, and marker sizes together so the chart stays
          visually balanced — Larger Element sizes work better with restricted
          depth ranges.
        </div>
      </div>

      {elementDescriptors.length > 0 && (
        <div className={style.section}>
          <div className={style.sectionTitle}>Chart Elements</div>
          <div className={style.elementToggles}>
            {elementDescriptors.map((descriptor) => {
              const label = descriptor.available ? (
                descriptor.label
              ) : (
                <>
                  {descriptor.label}{' '}
                  <span className={style.notShown}>(not shown on chart)</span>
                </>
              );
              return (
                <Checkbox
                  key={descriptor.key}
                  id={`export-image-element-${descriptor.key}`}
                  labelText={label}
                  checked={
                    !!value.elements[descriptor.key] && descriptor.available
                  }
                  disabled={!descriptor.available}
                  onChange={(_e, { checked }) =>
                    toggleElement(descriptor.key, checked)
                  }
                />
              );
            })}
          </div>
        </div>
      )}

      {supportedFormats.length > 1 && (
        <div className={style.section}>
          <div className={style.sectionTitle}>Output Format</div>
          <RadioButtonGroup
            legendText=""
            name="export-image-output-format"
            orientation="horizontal"
            valueSelected={value.outputFormat}
            onChange={(next) =>
              updateOptions({ outputFormat: next as OutputFormat })
            }
          >
            {OUTPUT_FORMAT_OPTIONS.filter((opt) =>
              supportedFormats.includes(opt.value)
            ).map((opt) => (
              <RadioButton
                key={opt.value}
                value={opt.value}
                labelText={opt.labelText}
                id={`export-image-format-${opt.value}`}
              />
            ))}
          </RadioButtonGroup>
        </div>
      )}
    </>
  );
}
